import { NavArrowRight } from "iconoir-react"
import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { CallMobileCard } from "./calls/CallHistory"

const navButtons = new Map([
  ["calls-nav-all", { id: "calls-nav-all", name: "All", status: "all" }],
  ["calls-nav-in-progress", { id: "calls-nav-in-progress", name: "In-progress", status: "in_progress" }],
  ["calls-nav-successful", { id: "calls-nav-successful", name: "Successful", status: "successful" }],
  ["calls-nav-scheduled", { id: "calls-nav-scheduled", name: "Scheduled", status: "scheduled" }],
  ["calls-nav-review", { id: "calls-nav-review", name: "Needs Review", status: "review" }]
])

function Nav({ setCallsStatus, initialActive }: { setCallsStatus: (status: string) => void; initialActive?: any }) {
  const [active, setActive] = useState("calls-nav-all")

  const clickButton = useCallback(
    (e: any) => {
      e.preventDefault()
      setActive(e.target.id)
      setCallsStatus(navButtons.get(e.target.id)?.status || "all")
    },
    [setCallsStatus]
  )

  useEffect(() => {
    setCallsStatus("all")
  }, [setCallsStatus])

  useEffect(() => {
    if (initialActive) {
      setActive(initialActive)
    }
  }, [initialActive])

  return (
    <div className="pb-1 -mb-1">
      <div className="flex flex-wrap gap-2 mb-4 w-fit mx-auto">
        {Array.from(navButtons.values()).map((b) => (
          <button
            key={b.id}
            id={b.id}
            onClick={clickButton}
            className={`grow min-w-fit flex-1 px-3 py-1 text-sm rounded-full border transition-all duration-200 flex items-center justify-center ${
              b.id === active
                ? "bg-purple-100 text-purple-800 font-medium border-purple-800"
                : "text-gray-600 hover:bg-gray-100 border-gray-300"
            }`}
          >
            {b.name}
          </button>
        ))}
      </div>
    </div>
  )
}

function List({ callSvcs: callSvcs, pageLimit }: { callSvcs: any; pageLimit: number }) {
  const [showAll, setShowAll] = useState(false)
  const [limit, setLimit] = useState(pageLimit)

  const showing = Math.min(limit, callSvcs?.length || 0)
  const total = callSvcs?.length || 0
  const MAX_SHOW_MORE = 100

  useEffect(() => {
    if (showAll) {
      setLimit(Math.min(total, pageLimit + MAX_SHOW_MORE))
    } else {
      setLimit(pageLimit)
    }
  }, [showAll, total, pageLimit])

  if (!callSvcs || callSvcs.length === 0) {
    return (
      <div className="flex justify-center items-center py-8 text-gray-500 bg-white rounded-xl shadow-sm p-6">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">No calls found</div>
          <p className="text-gray-400 text-sm">Your calls will appear here.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="space-y-3">
        {callSvcs?.slice(0, limit).map((svc: any) => (
          <Link
            to={`/calls/${svc.getCall().id}`}
            key={svc.getCall().id}
            className="block bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200"
          >
            {/* Desktop View */}
            {/* <div className="hidden md:flex items-center justify-between p-4">
              <div className="min-w-0 flex-1">
                <div className="text-purple-600 font-medium line-clamp-2 leading-snug">
                  {svc.getDescriptionShortFrontend()}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {svc.getCall().task_recipient_slug != "anyone" ? (
                    <div className="text-sm text-gray-500 truncate">{svc.getRecipientFrontendName()}</div>
                  ) : (
                    <div className="text-sm text-gray-500 truncate">
                      {formatAnyPhoneNumber(svc.getCall().country_code, svc.getCall().phone_number)}
                    </div>
                  )}
                  <span className="text-xs text-gray-400">•</span>
                  <div className="text-sm text-gray-500">{formatDuration(svc.getCall().duration)}</div>
                  <span className="text-xs text-gray-400">•</span>
                  <div className="text-sm text-gray-500">{svc.timeAgoStarted()}</div>
                </div>
                {svc.getFrontendStatus() === "scheduled" && (
                  <div className="text-sm text-blue-600 mt-1">Scheduled: {svc.getScheduledTime()}</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                {statusPills[svc.getFrontendStatus() as keyof typeof statusPills]}
                <NavArrowRight className="w-5 h-5 text-gray-400" />
              </div>
            </div> */}

            {/* Mobile View */}

            <CallMobileCard svc={svc} />
          </Link>
        ))}
      </div>

      {total > pageLimit && (
        <div className="flex justify-center items-center mt-4 pt-4">
          {showing < total && showing < pageLimit + MAX_SHOW_MORE ? (
            <button
              onClick={() => setShowAll(true)}
              className="text-sm text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-full font-medium shadow-sm transition-colors"
            >
              Show More ({showing} of {total})
            </button>
          ) : (
            <Link
              to="/calls"
              className="text-sm text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-full font-medium shadow-sm transition-colors flex items-center"
            >
              View Full Call History
              <NavArrowRight className="w-4 h-4 ml-1" />
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default function CallList({ callSvcsByStatus }: { callSvcsByStatus: any }): React.JSX.Element {
  const [callStatus, setCallStatus] = useState("all")
  const [initialActive] = useState("calls-nav-all")

  // Create a merged review category that includes both missing_data and failed statuses
  const mergedCallSvcsByStatus = {
    ...callSvcsByStatus,
    review: [...(callSvcsByStatus.missing_data || []), ...(callSvcsByStatus.failed || [])]
  }

  return (
    <div className="flex flex-col space-y-4">
      <Nav setCallsStatus={setCallStatus} initialActive={initialActive} />
      <List callSvcs={mergedCallSvcsByStatus[callStatus]} pageLimit={5} />
    </div>
  )
}
